// eslint-disable-next-line @altermaind/you-shall-not-pass
import { Dispatch, FC, SetStateAction, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Input, Svg, Typography } from '@altermaind/web/ui';
import { usePaginatedTable, PaginatedTable, Column } from '@/_shared/components';
import { getLocaleAndFormatDateFromTimeZone } from '@/_shared/helpers/getLocaleAndFormatDateFromTimeZone';
import { useNotification } from '@/_shared/hooks';
import { AccountStatus } from '@/swagger/constants/anagraphics-enums';
import { EmployerNotCompleted, getEmployeesCall, putEmployerByIdCall } from '../../../state';
import { PAGE_SIZE_IMPORTED_EMPLOYEES, VALID_EMAIL_REGEX } from '../../../state/constants';

type Props = {
  setSelectedProfile: (profile: EmployerNotCompleted | undefined) => void;
  selectedProfile: EmployerNotCompleted | undefined;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  setDisplayedData: Dispatch<SetStateAction<EmployerNotCompleted[]>>;
};

export const TableEmployees: FC<Props> = ({
  setSelectedProfile,
  selectedProfile,
  paginedProps,
  setDisplayedData,
}) => {
  const t = useTranslations('people.reviewAndFixEmployees.table');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { dateFormat } = getLocaleAndFormatDateFromTimeZone();
  const { loading: loadingGetEmployees } = getEmployeesCall.hook();
  const { execute: executePutEmployerById } = putEmployerByIdCall.hook();

  const updateEmployee = useCallback(
    (employee: EmployerNotCompleted) => {
      const internalEmployee = {
        ...employee,
        area: employee?.area?.name,
        department: employee?.department?.name,
        emailManager: employee?.manager?.email,
      };
      if (internalEmployee.id) {
        const oldPaginatedData = [...paginedProps.paginatedData];
        setDisplayedData(
          paginedProps.paginatedData.map(emp =>
            emp.id === internalEmployee.id ? internalEmployee : emp,
          ),
        );
        executePutEmployerById({
          ...internalEmployee,
          employeeId: internalEmployee.id || '',
          successSaga: ({ response }) => {
            setDisplayedData(
              paginedProps.paginatedData.map(emp =>
                emp.id === internalEmployee.id ? response : emp,
              ),
            );
          },
          errorSaga: response => {
            setDisplayedData(oldPaginatedData);
            const errorMap = {
              duplicateEmployee: '/problems/duplicate-employee',
              managerError: '/problems/manager-assignment',
            };
            if (response.error.type === errorMap.duplicateEmployee) {
              return notify(tShared('profileCard.editProfile.errors.emailAlreadyInUse'), 'error');
            }
            if (response.error.type === errorMap.managerError) {
              return notify(tShared('profileCard.editProfile.errors.managerError'), 'error');
            }
            return notify(tShared('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePutEmployerById, paginedProps.paginatedData, setDisplayedData, tShared],
  );

  const columns: Column[] = [
    {
      key: 'status',
      title: '',
      renderValue: (item: EmployerNotCompleted) => {
        const isError = item.issues?.length;
        return (
          <Box display='flex' alignItems='center'>
            <Box
              mr='xxs'
              size='m'
              borderRadius='100%'
              bg={isError ? 'error' : 'primary/interactive'}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <Svg name={isError ? 'close' : 'check'} size='s' />
            </Box>
          </Box>
        );
      },
      fitContent: true,
    },
    {
      key: 'name',
      title: t('name'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`firstName-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.firstName || ''}
            onBlur={({ target: { value } }) => {
              if (item.firstName !== value) {
                updateEmployee({ ...item, firstName: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'firstName') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'surname',
      title: t('surname'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`lastName-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.lastName || ''}
            onBlur={({ target: { value } }) => {
              if (item.lastName !== value) {
                updateEmployee({ ...item, lastName: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'lastName') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'email',
      title: t('email'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          {item.status === AccountStatus.ENABLED ? (
            <Typography variant='p2/light'>{item.email}</Typography>
          ) : (
            <Input
              name={`email-${item.id}`}
              type='text'
              placeholder='-'
              defaultValue={item.email || ''}
              onBlur={({ target: { value } }) => {
                if (item.email !== value && VALID_EMAIL_REGEX.test(value)) {
                  updateEmployee({ ...item, email: value });
                }
              }}
              backgroundColor='transparent'
              color={
                item.issues.find(issue => issue.fieldName === 'email') ||
                !VALID_EMAIL_REGEX.test(item?.email ?? '')
                  ? 'error'
                  : undefined
              }
            />
          )}
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 200,
      },
    },
    {
      key: 'birthDate',
      title: t('birthDate'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`birthDate-${item.id}`}
            type='text'
            placeholder='DD/MM/YYYY'
            defaultValue={item?.birthDate ? dayjs(item.birthDate).format(dateFormat) : ''}
            onBlur={({ target: { value } }) => {
              updateEmployee({ ...item, birthDate: value });
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => {
                return issue.fieldName === 'birthDate';
              })
                ? 'error'
                : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 120,
      },
    },
    {
      key: 'role',
      title: t('role'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`role-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.role || ''}
            onBlur={({ target: { value } }) => {
              if (item.role !== value) {
                updateEmployee({ ...item, role: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'role') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 180,
      },
    },
    {
      key: 'department',
      title: t('department'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`department-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.department?.name || ''}
            onBlur={({ target: { value } }) => {
              if (item.department?.name !== value) {
                updateEmployee({
                  ...item,
                  department: { id: item.department?.id || '', name: value },
                });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'department') ? 'error' : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'manager',
      title: t('manager'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`emailManager-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.manager?.email || ''}
            onBlur={({ target: { value } }) => {
              if (
                (item.manager?.email !== value && VALID_EMAIL_REGEX.test(value)) ||
                value === ''
              ) {
                updateEmployee({
                  ...item,
                  manager: { employeeId: item.manager?.employeeId || '', email: value },
                });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'manager') ||
              !VALID_EMAIL_REGEX.test(item?.manager?.email ?? '')
                ? 'error'
                : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 180,
      },
    },
    {
      key: 'area',
      title: t('area'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`area-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.area?.name || ''}
            onBlur={({ target: { value } }) => {
              if (item.area?.name !== value) {
                updateEmployee({ ...item, area: { id: item.area?.id || '', name: value } });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'area') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'fiscalCode',
      title: t('fiscalCode'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`fiscalCode-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.fiscalCode || ''}
            onBlur={({ target: { value } }) => {
              if (item.fiscalCode !== value) {
                updateEmployee({ ...item, fiscalCode: value });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'fiscalCode') ? 'error' : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 200,
      },
    },
    {
      key: 'jobLevel',
      title: t('jobLevel'),
      renderValue: (item: EmployerNotCompleted) => (
        <Box ml='-xs' display='flex' alignItems='center'>
          <Input
            name={`jobLevel-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.jobLevel || ''}
            onBlur={({ target: { value } }) => {
              if (item.jobLevel !== value) {
                updateEmployee({ ...item, jobLevel: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'jobLevel') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
  ];

  useEffect(() => {
    if (loadingGetEmployees) {
      setSelectedProfile(undefined);
    }
  }, [loadingGetEmployees, setSelectedProfile]);

  return (
    <PaginatedTable
      columns={columns}
      pageSize={PAGE_SIZE_IMPORTED_EMPLOYEES}
      call={{ ...getEmployeesCall }}
      onRowClick={(profile: EmployerNotCompleted) => {
        setSelectedProfile(profile);
      }}
      rowWrapperProps={(profile: EmployerNotCompleted) => {
        return profile.id === selectedProfile?.id
          ? { backgroundColor: 'grey/dark/background', borderRadius: '10px' }
          : {};
      }}
      paginedProps={paginedProps}
      loading={loadingGetEmployees}
      skeletonProps={{
        hasHeaderRow1: false,
        hasHeaderRow2: false,
        rows:
          !paginedProps?.currentPageNumber || paginedProps?.currentPageNumber <= 1 ? 5 : undefined,
      }}
      hasHover
    />
  );
};
